import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { usePartnerAuth } from '@/contexts/PartnerAuthContext';
import { Logo } from '@/components/Logo';

/**
 * Partner login page - handles magic link token validation
 * URL: /partner/login?token=xxx
 */
export function PartnerLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken, isAuthenticated, isLoading, error } = usePartnerAuth();

  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');
  const from = (location.state as { from?: string })?.from || '/partner';

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  // Auto-validate token if present in URL
  useEffect(() => {
    if (token && !isAuthenticated && !validating && !success) {
      handleValidateToken();
    }
  }, [token]);

  const handleValidateToken = async () => {
    if (!token) {
      setValidationError('No access token provided. Please use the link from your email.');
      return;
    }

    setValidating(true);
    setValidationError(null);

    const result = await loginWithToken(token);

    if (result) {
      setSuccess(true);
      // Short delay before redirect for UX
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1500);
    } else {
      setValidationError(error || 'Failed to validate access token. It may have expired.');
    }

    setValidating(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <Logo className="h-12" />
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Partner Portal
          </h1>
          <p className="text-center text-gray-600 mb-8">
            Access your referred projects and updates
          </p>

          {/* Validating State */}
          {validating && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="w-12 h-12 text-red-600 animate-spin" />
              <p className="text-gray-600">Validating your access...</p>
            </div>
          )}

          {/* Success State */}
          {success && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="bg-green-100 text-green-600 p-3 rounded-full">
                <CheckCircle size={32} />
              </div>
              <p className="text-green-600 font-medium">Access granted!</p>
              <p className="text-gray-600 text-sm">Redirecting to your dashboard...</p>
            </div>
          )}

          {/* Error State */}
          {validationError && !validating && !success && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-red-800 font-medium">Access Denied</p>
                    <p className="text-red-600 text-sm mt-1">{validationError}</p>
                  </div>
                </div>
              </div>

              {token && (
                <button
                  onClick={handleValidateToken}
                  className="w-full btn btn-primary"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* No Token State */}
          {!token && !validating && !success && !validationError && (
            <div className="py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-yellow-800 font-medium">No Access Token</p>
                    <p className="text-yellow-700 text-sm mt-1">
                      Please use the access link sent to your email to log in.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Help text */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              Need access? Contact your ACR representative.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
